import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef
} from 'react';

import debounce from 'lodash/debounce';
import { useRecoilValue } from 'recoil';

import { ia, iaRa, snakeToTitleCase } from 'lib/helpers/utility';
import { useInfiniteFetchData } from 'lib/hooks/useInfiniteFetchData';
import useTimezone from 'lib/hooks/useTimezone';

import NotFound from 'components/practice/Dashboard/components/NotFound';
import { currentPractice } from 'components/practice/practiceState';
import { showAlert } from 'components/shared/Alert/Alert';
import Icon from 'components/shared/Icon/Icon';
import Typography from 'components/shared/Typography/Typography';
import state from 'components/state';

import ActivitiesLogSkeleton from './ActivitiesLogSkeleton';
import ActivityLog from './components/ActivityLog';
import Comment from './components/Comment';
import CommentInput from './components/CommentInput';
import { exportActivitiesToPdf } from './lib/activityPdf/exportActivitiesToPdf';
import { expandActivityLogByIndex } from './lib/expandActivityLogByIndex';
import { formatChangedValues } from './lib/formatChangedValues';
import { formatDateHeader, groupActivitiesByDate } from './lib/groupActivitiesByDate';
import { useComment } from './useComment';

const ActivitiesLog = forwardRef((props, ref) => {
  const {
    id,
    fetchData,
    category,
    headerConfigs,
    customActivityFormatter = (activities) => activities,
    overwriteActivities = {},
    type
  } = props;

  const scrollRef = useRef(null);
  const observer = useRef(null);
  const userState = useRecoilValue(state.userState);
  const permission = useRecoilValue(state.permissions);
  const timezone = useTimezone();
  const practice = useRecoilValue(currentPractice);

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
    refetch,
    resetRefParams
  } = useInfiniteFetchData({
    queryKey: 'activities',
    queryFn: fetchData,
    params: { id, type },
    dependencies: [category, id],
    options: { staleTime: Infinity }
  });

  const { isCreatingComment, onCommentPostHandler, onCommentDeleteHandler, onCommentSaveHandler } =
    useComment(category, id);

  const getFinalActivities = (propData = data) => {
    const expandedActivities = expandActivityLogByIndex(iaRa(propData)).sort(
      (a, b) => new Date(a.created_at) - new Date(b.created_at)
    );

    const customActivities = customActivityFormatter(expandedActivities);

    const formattedActivities = formatChangedValues({ activities: customActivities });

    const groupedActivities = groupActivitiesByDate(formattedActivities, timezone);

    return groupedActivities;
  };

  const finalGroupedActivities = useMemo(() => getFinalActivities(), [data, timezone]);

  const handleExportPdf = useCallback(async () => {
    try {
      const newData = await refetch({ params: { forPdfExport: true } });
      resetRefParams();

      const groupedActivities = getFinalActivities(newData);

      const title = `${snakeToTitleCase(category) || ''} activity log`;

      await exportActivitiesToPdf({
        groupedActivities,
        title,
        category,
        headerConfigs: { ...headerConfigs, practice }
      });
    } catch (error) {
      console.error('Failed to export an activity log to a PDF:', error);
      showAlert({ message: 'Failed to export an activity log to a PDF', color: 'danger' });
    }
  }, [refetch, category, practice]);

  useImperativeHandle(ref, () => ({ handleExportPdf }), [handleExportPdf]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [isLoading, isCreatingComment]);

  const firstItemRef = useCallback(
    (node) => {
      if (isLoading || isFetchingNextPage) return;
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver(
        debounce((entries) => {
          if (entries[0].isIntersecting && hasNextPage) {
            const scrollContainer = scrollRef.current;
            if (scrollContainer) {
              const firstItemOffset = node?.offsetTop ?? 0;
              const previousScrollHeight = scrollContainer.scrollHeight;

              fetchNextPage().then(() => {
                requestAnimationFrame(() => {
                  const newScrollHeight = scrollContainer.scrollHeight;
                  const heightDifference = newScrollHeight - previousScrollHeight;
                  scrollContainer.scrollTop = firstItemOffset + heightDifference;
                });
              });
            }
          }
        }, 200)
      );

      if (node) observer.current.observe(node);
    },
    [isLoading, isFetchingNextPage, hasNextPage, fetchNextPage]
  );

  if (isLoading) return <ActivitiesLogSkeleton />;

  if (isError) {
    return (
      <NotFound
        className="justify-start pt-16"
        icon="illustration-no-content"
        title="Unable to load activities"
        description="Please try again later."
      />
    );
  }

  return (
    <>
      <div
        ref={scrollRef}
        className="-mt-1 flex flex-1 flex-col justify-between gap-5 overflow-y-auto px-5 pb-5 pt-0">
        {isFetchingNextPage && (
          <div className="align-center flex justify-center pb-2">
            <Icon icon="loader-linear" className="animate-spin" color="primary" size={28} />
          </div>
        )}
        <div className="flex flex-col gap-7">
          {!ia(data) ? (
            <NotFound
              className="justify-start pt-16"
              icon="illustration-no-content"
              description="No activities yet"
              descriptionClassName="text-base text-neutral-900"
            />
          ) : (
            finalGroupedActivities.dateKeys.map((dateKey, dateIndex) => {
              const activities = finalGroupedActivities.grouped[dateKey];
              const isFirstDateGroup = dateIndex === 0;

              return (
                <div key={dateKey} className="flex flex-col">
                  <div className="sticky top-0 z-10 w-full border-b border-neutral-200 bg-white p-4 pl-0 pt-1">
                    <Typography size="small" weight="medium" color="neutral-800">
                      {formatDateHeader(activities[0]?.created_at, timezone)}
                    </Typography>
                  </div>

                  <div className="flex flex-col gap-5">
                    {iaRa(activities).map((activity, activityIndex) => {
                      const isFirstItem = isFirstDateGroup && activityIndex === 0;

                      return (
                        <div
                          key={activity.id || `${dateKey}-${activityIndex}`}
                          ref={isFirstItem ? firstItemRef : null}>
                          {activity.type === 'comment' ? (
                            <Comment
                              comment={activity}
                              category={category}
                              refId={id}
                              onDelete={onCommentDeleteHandler}
                              onSave={onCommentSaveHandler}
                              creatorId={permission?.admin_id || userState?.id}
                            />
                          ) : (
                            <ActivityLog
                              overwriteActivities={overwriteActivities}
                              category={category}
                              activity={activity}
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      <div className="border-t-200 flex flex-col gap-2 border-b-0 border-l-0 border-r-0 border-solid border-neutral-200 p-3">
        <CommentInput
          onPost={onCommentPostHandler}
          loading={isCreatingComment}
          placeholder="Comment global"
        />

        {/* <Typography size="small" color="neutral-500" className="px-1">
          use @ to mention someone
        </Typography> */}
      </div>
    </>
  );
});

ActivitiesLog.displayName = 'ActivitiesLog';

export default ActivitiesLog;
